import type {
  StarterCoordinationResponse,
  StarterCoordinationWarning,
} from "../services/starterCoordination";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = StarterCoordinationWarning["code"];

type WarningGroup =
  | "not-met"
  | "review-evidence"
  | "fault-level"
  | "not-evaluated"
  | "declared-evidence";

interface StarterCoordinationWarningPanelProps {
  result: StarterCoordinationResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error. A
// starter coordination warning carries only a code and a message - there is no
// severity and no reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  IQ_BELOW_PROSPECTIVE_CURRENT: "Iq below the prospective current",
  DECLARED_TYPE_BELOW_REQUIRED: "Declared coordination type below the required type",
  TEST_VOLTAGE_BELOW_SYSTEM: "Certificate test voltage below the system voltage",
  CONTACTOR_AC3_BELOW_FLC: "Contactor AC-3 rating below the full-load current",
  FLC_OUTSIDE_OVERLOAD_RANGE: "Full-load current outside the overload range",
  IQ_NOT_ESTABLISHED: "Certified Iq not established",
  TEST_VOLTAGE_NOT_ESTABLISHED: "Test voltage not established",
  CERTIFICATE_REFERENCE_NOT_ESTABLISHED: "Certificate reference not established",
  STARTER_CURRENT_CHECKS_NOT_EVALUATED: "Starter current checks not evaluated",
  PROSPECTIVE_CURRENT_NOT_LINKED: "Prospective current not linked to a fault run",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "Fault run not a maximum three-phase study",
  FAULT_RUN_BUS_MISMATCH: "Fault run bus differs from the study bus",
  COORDINATION_EVIDENCE_MANUFACTURER_DECLARED: "Coordination evidence manufacturer-declared",
};

// Where each code is listed. A starter that failed a check comes first (there is
// nothing to accept), then the certificate evidence the engineer still owes - a missing
// item, or a test voltage below the system voltage (IR-001 F1) - then the
// fault level that is not the linked maximum three-phase study of the bus, then
// what the method does not evaluate, and last the declared-evidence limit that
// every result carries (A20 (g), GAP-020).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  IQ_BELOW_PROSPECTIVE_CURRENT: "not-met",
  DECLARED_TYPE_BELOW_REQUIRED: "not-met",
  TEST_VOLTAGE_BELOW_SYSTEM: "review-evidence",
  CONTACTOR_AC3_BELOW_FLC: "not-met",
  FLC_OUTSIDE_OVERLOAD_RANGE: "not-met",
  IQ_NOT_ESTABLISHED: "review-evidence",
  TEST_VOLTAGE_NOT_ESTABLISHED: "review-evidence",
  CERTIFICATE_REFERENCE_NOT_ESTABLISHED: "review-evidence",
  PROSPECTIVE_CURRENT_NOT_LINKED: "fault-level",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "fault-level",
  FAULT_RUN_BUS_MISMATCH: "fault-level",
  STARTER_CURRENT_CHECKS_NOT_EVALUATED: "not-evaluated",
  COORDINATION_EVIDENCE_MANUFACTURER_DECLARED: "declared-evidence",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Coordination not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "fault-level", title: "Fault level (review required)" },
  { group: "not-evaluated", title: "Not evaluated" },
  { group: "declared-evidence", title: "Declared evidence" },
];

// One listed warning: a row's warning names its starter, a study warning stands alone.
type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The study's warnings followed by every row's, in the engine's order, rows named. */
export function listWarnings(result: StarterCoordinationResponse): ListedWarning[] {
  const study = result.warnings.map((warning, index) => ({
    key: `study-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const rows = result.rows.flatMap((row) =>
    row.warnings.map((warning, index) => ({
      key: `${row.motor_code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Starter ${row.motor_code}: `,
    })),
  );
  return [...study, ...rows];
}

export function StarterCoordinationWarningPanel({
  result,
}: StarterCoordinationWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            // Within a group the study's order, then each row's own order, is kept.
            const members = warnings.filter(
              (warning) => WARNING_CODE_GROUPS[warning.code] === group,
            );
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default StarterCoordinationWarningPanel;
